import type { Entry, RecordConversation } from "../../server/core/conversations";
import { countLine } from "../../server/rules/list-page";
import { openViewer } from "./viewer";
import { composer, entryView, lastFromUs, threadView, type Mode } from "./conversation";
import { live } from "../shell/live";
import { api, el, fileName, toast } from "./dom";
import { icon } from "./icons";
import { searchBox } from "./list";
import { chip } from "./record";

// The Messages card on a client, person or patient record (brief 1B-5, mockup's record Messages):
// text, email or call them from a line, or leave a note for the team; replies land here. It works
// with the shared inbox on or off. Brief 1E-34 (mockup v92): one history per person: staff texts,
// emails, calls and notes, automatic texts, secure links, chat about a visit and her notes from
// the field app, each saying who sent it or Automatic, the channel, the visit and where it went
// with its status. Filters All, Staff, Automatic with counts, a search, pages, and Forward.

type Res = { ok: boolean; status: number; body: RecordConversation & { error?: string } };
type Filter = "all" | "staff" | "auto";

const SENT: Record<Mode, [string, string]> = {
  text: ["Sent", "By text · replies land here"],
  email: ["Sent", "By email · replies land here"],
  call: ["Calling…", "The caller's phone rings first, then the call connects"],
  note: ["Note added", "Only the team sees it"],
};

const FILTERS: [Filter, string][] = [
  ["all", "All"],
  ["staff", "Staff"],
  ["auto", "Automatic"],
];

const CHANNEL_LEVEL: Record<string, string> = {
  Text: "info",
  Email: "info",
  Note: "warn",
  App: "plain",
  Call: "plain",
};

export function messagesCard(
  kind: "client" | "person" | "patient",
  id: string,
  changed: () => void,
): HTMLElement {
  const card = el(
    "section",
    { class: "card", "aria-label": "Messages" },
    el("h2", {}, icon("comment-discussion"), " Messages"),
    el("p", { class: "muted" }, "Loading…"),
  );
  let mode: Mode | null = null;
  let stamp = "";
  // What the card asks for (brief 1E-34): the filter, the search and the page.
  const q = { chip: "all" as Filter, text: "", page: 1 };
  /** The entry whose Forward form is open, and what's picked in it (kept across a redraw). */
  let forwarding: string | null = null;
  let draft: { how: string; to: string; line: string } = { how: "", to: "", line: "" };
  // What a live update redraws in place (brief 1B-6): the thread, never the composer.
  const threadSlot = el("div");
  const filterSlot = el("div", { class: "seg", role: "group", "aria-label": "Show" });
  const pagerSlot = el("div", { class: "pager", "data-unsaved": "off" });
  const search = searchBox("Search messages", (text) => {
    q.text = text;
    q.page = 1;
    void reload();
  });
  // Brief 1E-32: the reply box under the heading, then the newest message; earlier ones fold.
  const fold = { all: false };

  const params = () => {
    const p = new URLSearchParams();
    if (q.chip !== "all") p.set("chip", q.chip);
    if (q.text.trim()) p.set("q", q.text.trim());
    p.set("page", String(q.page));
    p.set("per", "50");
    return p;
  };
  const path = () => `/api/conversations/${kind}/${encodeURIComponent(id)}?${params().toString()}`;

  async function reload() {
    const res = (await api<RecordConversation>("GET", path())) as Res;
    if (res.ok && card.isConnected) drawThread(res.body);
  }

  function drawFilters(v: RecordConversation) {
    filterSlot.replaceChildren(
      ...FILTERS.map(([k, label]) => {
        const b = el(
          "button",
          {
            type: "button",
            class: `btn sm${q.chip === k ? " pri" : ""}`,
            "aria-pressed": String(q.chip === k),
            "data-filter": k,
          },
          `${label} `,
          el("span", { class: "muted" }, String(v.counts[k])),
        );
        b.addEventListener("click", () => {
          q.chip = k;
          q.page = 1;
          void reload().then(() =>
            filterSlot.querySelector<HTMLElement>(`[data-filter="${k}"]`)?.focus(),
          );
        });
        return b;
      }),
    );
  }

  function drawPager(v: RecordConversation) {
    if (v.page.pages <= 1) return pagerSlot.replaceChildren();
    const step = (label: string, to: number, can: boolean) => {
      const b = el("button", { type: "button", class: "btn sm", disabled: !can }, label);
      b.addEventListener("click", () => {
        q.page = to;
        void reload();
      });
      return b;
    };
    pagerSlot.replaceChildren(
      el("span", { class: "small pagercount", "aria-live": "polite" }, countLine(v.page)),
      step("Newer", v.page.page - 1, v.page.page > 1),
      step("Older", v.page.page + 1, v.page.page < v.page.pages),
    );
  }

  function drawThread(v: RecordConversation) {
    stamp = v.stamp;
    drawFilters(v);
    drawPager(v);
    threadSlot.replaceChildren(
      threadView(
        v.entries,
        fileChip,
        fold,
        (m) => historyEntry(m, v),
        q.chip === "all" && !q.text ? "No messages yet." : "Nothing here.",
      ),
    );
  }

  /** One entry of the history: who or Automatic, the channel, when, the visit; where it went. */
  function historyEntry(m: Entry, v: RecordConversation): HTMLElement {
    // Notes, calls and their voicemails draw as the thread always has, with Forward added.
    const base =
      m.kind === "note" || m.kind === "call"
        ? entryView(m, fileChip)
        : el(
            "div",
            { class: `msg${m.kind !== "in" ? " out" : ""}`, role: "listitem", "data-entry": m.id },
            el(
              "span",
              { class: "meta" },
              m.auto ? icon("zap") : "",
              m.auto ? ` Automatic · ${m.auto}` : m.who,
              " · ",
              chip(m.channelLabel, CHANNEL_LEVEL[m.channelLabel] ?? "plain"),
              m.kind === "out" && !m.auto && m.line ? ` · from ${m.line}` : "",
              ` · ${m.when}`,
            ),
            el("span", { class: "body" }, m.body),
            m.attachments.length
              ? el(
                  "span",
                  { class: "linkrow", style: "margin:4px 0 0" },
                  ...m.attachments.map(fileChip),
                )
              : null,
            m.dest
              ? el("div", { class: "small muted" }, m.dest)
              : m.kind === "in" && m.status
                ? el("span", { class: "meta" }, chip(m.status.label, m.status.level))
                : null,
          );
    if (m.visit) {
      const meta = base.querySelector(".meta");
      meta?.append(" · ", chip(`Visit ${m.visit.label}`, "plain"));
    }
    if (!v.canEdit) return base;
    const fwd = el(
      "button",
      { type: "button", class: "btn ib sm", "aria-label": "Forward", "data-tip": "Forward" },
      icon("share"),
    );
    fwd.addEventListener("click", () => {
      forwarding = forwarding === m.id ? null : m.id;
      draft = { how: "", to: "", line: "" };
      drawThread(v);
      card
        .querySelector<HTMLElement>(
          `[data-entry="${CSS.escape(m.id)}"] select, [data-entry="${CSS.escape(m.id)}"] .btn`,
        )
        ?.focus();
    });
    base.append(el("div", { class: "actions", style: "margin-top:4px" }, fwd));
    if (forwarding === m.id) base.append(forwardForm(m, v));
    return base;
  }

  /** Forward (mockup v92): To, How and a line to add; it goes as a new message on this record. */
  function forwardForm(m: Entry, v: RecordConversation): HTMLElement {
    const how = el(
      "select",
      { id: `fw-how-${m.id}` },
      ...(v.compose.to.email.length && v.compose.from.email.length
        ? [el("option", { value: "email" }, "Email")]
        : []),
      ...(v.compose.to.text.length && v.compose.from.text.length
        ? [el("option", { value: "text" }, "Text")]
        : []),
      el("option", { value: "note" }, "Note to the team"),
    );
    const to = el("select", { id: `fw-to-${m.id}` });
    if (draft.how && [...how.options].some((o) => o.value === draft.how)) how.value = draft.how;
    const fillTo = () => {
      const h = how.value as "email" | "text" | "note";
      to.replaceChildren(
        ...(h === "note"
          ? [el("option", { value: "" }, "Someone on the team")]
          : v.compose.to[h].map((x) => el("option", { value: x.value }, x.label))),
      );
      if (draft.to && [...to.options].some((o) => o.value === draft.to)) to.value = draft.to;
    };
    how.addEventListener("change", () => {
      draft.how = how.value;
      fillTo();
    });
    to.addEventListener("change", () => (draft.to = to.value));
    fillTo();
    const line = el("input", { id: `fw-line-${m.id}`, type: "text", maxlength: "500" });
    line.value = draft.line;
    line.addEventListener("input", () => (draft.line = line.value));
    const go = el("button", { type: "submit", class: "btn sm pri" }, icon("share"), " Forward");
    const cancel = el("button", { type: "button", class: "btn sm" }, "Cancel");
    cancel.addEventListener("click", () => {
      forwarding = null;
      drawThread(v);
    });
    const form = el(
      "form",
      { class: "form", style: "max-width:none;margin-top:6px", "aria-label": "Forward" },
      el(
        "div",
        { class: "grid3", style: "gap:8px" },
        el("div", { class: "field" }, el("label", { for: to.id }, "To"), to),
        el("div", { class: "field" }, el("label", { for: how.id }, "How"), how),
        el("div", { class: "field" }, el("label", { for: line.id }, "Add a line"), line),
      ),
      el("div", { class: "actions" }, go, cancel),
    );
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      go.disabled = true;
      const h = how.value as "email" | "text" | "note";
      const res = (await api<RecordConversation>(
        "POST",
        `/api/conversations/${kind}/${encodeURIComponent(id)}/forward?${params().toString()}`,
        {
          entry: m.id,
          how: h,
          to: h === "note" ? null : to.value,
          from: h === "note" ? null : (v.compose.from[h][0]?.id ?? null),
          line: line.value,
        },
      )) as Res;
      go.disabled = false;
      if (!res.ok) return toast("That wasn't forwarded", res.body.error ?? "Try again.");
      forwarding = null;
      toast("Forwarded", h === "note" ? "As a note to the team" : `By ${h}`);
      drawThread(res.body);
      changed();
    });
    return form;
  }

  function draw(v: RecordConversation) {
    mode ??= v.compose.to.text.length ? "text" : "email";
    const box = v.canEdit
      ? composer({
          compose: v.compose,
          mode,
          inbox: false,
          last: lastFromUs(v.entries),
          onMode: (m) => (mode = m),
          send: async (b) => {
            const res = (await api<RecordConversation>(
              "POST",
              `/api/conversations/${kind}/${id}/send?${params().toString()}`,
              b,
            )) as Res;
            if (!res.ok) return res.body.error ?? "That wasn't sent.";
            const [what, sub] = SENT[b.mode];
            toast(what, sub);
            drawThread(res.body);
            changed();
            return null;
          },
        }).el
      : null;
    card.replaceChildren(
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, icon("comment-discussion"), " Messages"),
          el(
            "span",
            { class: "small muted" },
            "Everything sent to or about them. Replies land here.",
          ),
        ),
      ),
      ...(box ? [box] : []),
      el(
        "div",
        { class: "row msgbar", style: "gap:8px;flex-wrap:wrap;align-items:center" },
        filterSlot,
        search,
      ),
      threadSlot,
      pagerSlot,
    );
    drawThread(v);
  }

  void (async () => {
    const res = (await api<RecordConversation>("GET", path())) as Res;
    if (!res.ok) return card.remove();
    draw(res.body);
    // Live (brief 1B-6): with the shared inbox on or off, new messages, calls and voicemails land
    // here without a refresh, asked every 15 seconds while the tab is visible.
    live.watch(`messages:${kind}:${id}`, async () => {
      if (!card.isConnected) return false;
      if (forwarding) return true;
      const got = (await api<RecordConversation | { same: true }>(
        "GET",
        `${path()}&stamp=${encodeURIComponent(stamp)}`,
        undefined,
        { background: true },
      )) as { ok: boolean; body: RecordConversation | { same: true } };
      if (!got.ok || !card.isConnected || "same" in got.body) return card.isConnected;
      drawThread(got.body);
      return true;
    });
  })();
  return card;
}

/** A file in the conversation (an attachment, a voicemail): open it. */
const fileChip = (a: { id: string; name: string }) => {
  const b = el("button", { type: "button", class: "chipbtn" }, icon("paperclip"), fileName(a.name));
  b.addEventListener("click", () => void openViewer({ file: { id: a.id, name: a.name } }));
  return b;
};
